/**
 * A received email, as the document of a sandboxed <iframe srcdoc>.
 *
 * The ERP never puts a stranger's HTML in its own page. The body is drawn in a
 * frame that the reader component creates with `sandbox="allow-same-origin
 * allow-popups allow-popups-to-escape-sandbox"`: no scripts and no forms. The
 * same origin is there ONLY so the reader can measure the height of what it
 * drew; without `allow-scripts` nothing inside can use it (the dangerous pair
 * is the two together, and `allow-scripts` is never given). On top of that,
 * the document carries its own Content Security Policy as its FIRST element,
 * so nothing inside can load from the network unless the person asked for it:
 *
 *   default-src 'none'          nothing by default
 *   img-src data: [https: http:] embedded images always; remote ones only after
 *                                «Mostrar imágenes» (a remote image is how a
 *                                sender learns that, when and where it was read)
 *   style-src 'unsafe-inline'   the email's own styling
 *   font-src data:
 *
 * A second policy inside the email can only narrow this one: browsers apply
 * every policy they find, never the most lenient. And a later <base> is
 * ignored: the first one wins, so links open in a new tab.
 *
 * Pure function, no DOM: it is tested in Node (tests/frame.test.js).
 */

/**
 * @param {string} html            the body as it came
 * @param {object} [options]
 * @param {boolean} [options.remoteImages]  load images from the network
 * @param {'light'|'dark'} [options.scheme] the colours the reader is in
 * @returns {string}
 */
export function frameDocument(html, { remoteImages = false, scheme = 'light' } = {}) {
  const images = remoteImages ? 'data: https: http: cid:' : 'data: cid:'
  const policy = `default-src 'none'; img-src ${images}; style-src 'unsafe-inline'; font-src data:; media-src data:`
  const dark = scheme === 'dark'
  return [
    '<!doctype html><html><head>',
    `<meta http-equiv="Content-Security-Policy" content="${policy}">`,
    '<meta charset="utf-8">',
    '<base target="_blank">',
    '<meta name="referrer" content="no-referrer">',
    '<style>',
    'html,body{margin:0;padding:0}',
    `body{padding:16px 4px;font:14px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Arial,sans-serif;color:${dark ? '#e6e8eb' : '#1f2328'};background:${dark ? '#16181c' : '#ffffff'};word-wrap:break-word;overflow-wrap:anywhere}`,
    'img{max-width:100%;height:auto}',
    'table{max-width:100%}',
    `a{color:${dark ? '#7cb4ff' : '#0a66c2'}}`,
    `blockquote{margin:8px 0;padding-left:12px;border-left:3px solid ${dark ? '#3a3f47' : '#d0d7de'};color:${dark ? '#aab0b8' : '#57606a'}}`,
    'pre{white-space:pre-wrap}',
    '</style>',
    '</head><body>',
    String(html ?? ''),
    '</body></html>',
  ].join('')
}

/** Whether the body asks for anything from the network — to offer «Mostrar imágenes». */
export function hasRemoteImages(html) {
  return /<img\b[^>]*\bsrc\s*=\s*["']?\s*https?:/i.test(String(html ?? ''))
    || /url\(\s*["']?\s*https?:/i.test(String(html ?? ''))
}

/** Plain text as a body the frame can draw: escaped, links clickable, lines kept. */
export function textDocument(text) {
  const escaped = String(text ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1">$1</a>')
  return `<pre style="font:inherit;margin:0">${escaped}</pre>`
}
